import { useState } from "react";
import { toast } from "sonner";
import { Send, Trophy, ScrollText, ChevronDown, ChevronLeft, ChevronRight, Gem, TrendingUp } from "lucide-react";
import { GoldCoin, FollisCoin } from "@/components/CoinIcons";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { btn } from "@/components/Page";

const LOG_META = {
  donate: { Icon: GoldCoin, cls: "text-emerald-400" },
  send: { Icon: Send, cls: "text-sky-400" },
  upgrade: { Icon: Trophy, cls: "text-amber-400" },
  cosmetic: { Icon: Gem, cls: "text-fuchsia-400" }
};

export function Treasury({ a, reload }) {
  const { refreshUser } = useAuth();
  const [amount, setAmount] = useState("");
  const [sendTo, setSendTo] = useState("");
  const [sendAmt, setSendAmt] = useState("");
  const [busy, setBusy] = useState(false);
  const [log, setLog] = useState(null);
  const [showLog, setShowLog] = useState(false);
  const [logPage, setLogPage] = useState(1);
  const [logTotalPages, setLogTotalPages] = useState(1);
  const [isOpen, setIsOpen] = useState(false); // New: collapsed by default
  const dons = a.donations_recent || [];
  const top = a.top_contributors || [];

  const loadLog = async (page = 1) => {
    try {
      const { data } = await api.get(`/alliance/treasury/log?page=${page}`);
      setLog(data.log || []);
      setLogPage(data.page || 1);
      setLogTotalPages(data.total_pages || 1);
    } catch { setLog([]); setLogTotalPages(1); }
  };
  const toggleLog = () => { const next = !showLog; setShowLog(next); if (next) loadLog(1); };

  const donate = async () => {
    const n = parseInt(amount, 10);
    if (!n || n < 1) return toast.error("Enter an amount of at least 1 gold");
    setBusy(true);
    try {
      await api.post("/alliance/treasury/donate", { amount: n });
      toast.success(`Donated ${n.toLocaleString()} gold to the treasury`, { icon: <GoldCoin size={18} className="text-amber-400" /> });
      setAmount(""); reload(); refreshUser(); if (showLog) loadLog(1);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  const sendGold = async () => {
    const n = parseInt(sendAmt, 10);
    if (!sendTo) return toast.error("Pick a member to send gold to");
    if (!n || n < 1) return toast.error("Enter an amount of at least 1 gold");
    setBusy(true);
    try {
      await api.post("/alliance/treasury/send", { user_id: sendTo, amount: n });
      toast.success(`Sent ${n.toLocaleString()} gold from the treasury`, { icon: <Send size={18} className="text-sky-400" /> });
      setSendAmt(""); setSendTo(""); reload(); refreshUser(); if (showLog) loadLog(1);
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <section className="border border-amber-900/30 bg-zinc-950 p-5" data-testid="alliance-treasury">
      <button 
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between hover:bg-zinc-900/30 -m-5 p-5 transition-colors"
      >
        <h3 className="font-cinzel text-amber-300 uppercase tracking-wide text-sm flex items-center gap-2">
          <GoldCoin size={16} /> Treasury
          <span className="text-zinc-500 normal-case tracking-normal text-xs ml-2">
            {(a.treasury_gold || 0).toLocaleString()}g
          </span>
        </h3>
        <ChevronDown size={18} className={`text-amber-500 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <div className="mt-5">
          <div className="flex items-center gap-2 mb-4">
            <GoldCoin size={22} className="text-amber-500" />
            <span data-testid="treasury-balance" className="font-cinzel text-3xl text-amber-300 tabular-nums">{(a.treasury_gold || 0).toLocaleString()}</span>
            <span className="text-xs uppercase tracking-widest text-zinc-500 mt-1.5">gold</span>
          </div>

      <div className="flex flex-wrap items-end gap-3 mb-5">
        <div className="flex-1 min-w-[160px]">
          <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Donate gold</label>
          <input data-testid="treasury-donate-input" type="number" min={1} value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Amount"
            className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-amber-600 outline-none" />
        </div>
        <button data-testid="treasury-donate-btn" disabled={busy} onClick={donate} className={btn("primary") + " py-2.5 flex items-center gap-1.5"}><GoldCoin size={15} /> Donate</button>
      </div>

      {a.can_treasury && (
        <div className="border border-sky-900/40 bg-sky-950/10 px-4 py-4 mb-5" data-testid="treasury-send-section">
          <h4 className="text-[11px] uppercase tracking-widest text-sky-300/90 mb-3 flex items-center gap-1.5"><Send size={13} /> Send gold to a member</h4>
          <div className="flex flex-wrap items-end gap-3">
            <div className="flex-1 min-w-[140px]">
              <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Member</label>
              <select data-testid="treasury-send-member" value={sendTo} onChange={(e) => setSendTo(e.target.value)}
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-sky-600 outline-none">
                <option value="">— select —</option>
                {(a.members || []).map((m) => (
                  <option key={m.user_id} value={m.user_id}>{m.name}{m.is_leader ? " (you)" : ""}</option>
                ))}
              </select>
            </div>
            <div className="flex-1 min-w-[120px]">
              <label className="block text-[11px] uppercase tracking-widest text-zinc-500 mb-1">Amount</label>
              <input data-testid="treasury-send-input" type="number" min={1} value={sendAmt} onChange={(e) => setSendAmt(e.target.value)} placeholder="Amount"
                className="w-full bg-zinc-900 border border-zinc-800 px-3 py-2.5 text-zinc-100 text-sm focus:border-sky-600 outline-none" />
            </div>
            <button data-testid="treasury-send-btn" disabled={busy} onClick={sendGold}
              className="py-2.5 px-4 rounded-sm bg-sky-600 hover:bg-sky-500 text-white text-sm font-cinzel uppercase tracking-wide transition-colors flex items-center gap-1.5 disabled:opacity-50"><Send size={15} /> Send</button>
          </div>
        </div>
      )}

      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <h4 className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><Trophy size={13} className="text-amber-500/70" /> Top Contributors</h4>
          {top.length === 0 ? <p className="text-xs text-zinc-600 italic">No donations yet.</p> : (
            <div className="space-y-1.5" data-testid="treasury-top-contributors">
              {top.map((c, i) => (
                <div key={c.user_id} className="flex items-center gap-2 text-sm">
                  <span className={`font-cinzel w-5 text-center ${i === 0 ? "text-amber-300" : i === 1 ? "text-zinc-300" : "text-amber-700"}`}>{i + 1}</span>
                  <span className="text-zinc-200 truncate">{c.name}</span>
                  <span className="ml-auto text-amber-400 tabular-nums flex items-center gap-1"><GoldCoin size={12} />{c.total.toLocaleString()}</span>
                </div>
              ))}
            </div>
          )}
        </div>
        <div>
          <h4 className="text-[11px] uppercase tracking-widest text-zinc-500 mb-2 flex items-center gap-1.5"><GoldCoin size={13} className="text-amber-500/70" /> Recent Donations</h4>
          {dons.length === 0 ? <p className="text-xs text-zinc-600 italic">No donations yet.</p> : (
            <div className="space-y-1.5" data-testid="treasury-recent-donations">
              {dons.map((d) => (
                <div key={d.id} className="flex items-center gap-2 text-sm border-b border-zinc-900 pb-1.5">
                  <span className="text-zinc-200 truncate">{d.name}</span>
                  <span className="text-amber-400 tabular-nums flex items-center gap-1">+{d.amount.toLocaleString()}</span>
                  <span className="ml-auto text-[10px] text-zinc-600">{new Date(d.at).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {a.can_treasury && (
        <div className="mt-5 pt-4 border-t border-zinc-800" data-testid="treasury-log-section">
          <button data-testid="treasury-log-toggle" onClick={toggleLog}
            className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 transition-colors">
            <ScrollText size={13} /> Treasury Activity
            <ChevronDown size={13} className={`transition-transform ${showLog ? "rotate-180" : ""}`} />
          </button>
          {showLog && (
            <div className="mt-3" data-testid="treasury-log">
              {log === null ? <p className="text-xs text-zinc-600 italic">Loading…</p>
                : log.length === 0 ? <p className="text-xs text-zinc-600 italic">No treasury activity yet.</p> : (
                  <div className="space-y-1.5">
                    {log.map((e) => {
                      const meta = LOG_META[e.kind] || { Icon: TrendingUp, cls: "text-zinc-400" };
                      const Icon = meta.Icon;
                      const pos = e.delta >= 0;
                      return (
                        <div key={e.id} data-testid={`treasury-log-row-${e.id}`} className="flex items-center gap-2 text-sm border-b border-zinc-900 pb-1.5">
                          <Icon size={13} className={`${meta.cls} shrink-0`} />
                          <span className="text-zinc-300 min-w-0"><span className="text-zinc-100">{e.actor_name}</span> <span className="text-zinc-500">· {e.detail}</span></span>
                          <span className={`ml-auto tabular-nums shrink-0 ${pos ? "text-emerald-400" : "text-red-400"}`}>{pos ? "+" : "−"}{Math.abs(e.delta).toLocaleString()}</span>
                          <span className="text-[10px] text-zinc-600 shrink-0 w-14 text-right">{new Date(e.at).toLocaleDateString([], { month: "short", day: "numeric" })}</span>
                        </div>
                      );
                    })}
                  </div>
                )}
              {log && log.length > 0 && logTotalPages > 1 && (
                <div className="flex items-center justify-between mt-3 pt-2 border-t border-zinc-900" data-testid="treasury-log-pagination">
                  <button data-testid="treasury-log-prev" onClick={() => loadLog(logPage - 1)} disabled={logPage <= 1}
                    className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 disabled:opacity-30 disabled:hover:text-zinc-400 transition-colors">
                    <ChevronLeft size={13} /> Prev
                  </button>
                  <span className="text-[11px] uppercase tracking-widest text-zinc-500 tabular-nums" data-testid="treasury-log-page-indicator">Page {logPage} of {logTotalPages}</span>
                  <button data-testid="treasury-log-next" onClick={() => loadLog(logPage + 1)} disabled={logPage >= logTotalPages}
                    className="flex items-center gap-1 text-[11px] uppercase tracking-widest text-zinc-400 hover:text-amber-300 disabled:opacity-30 disabled:hover:text-zinc-400 transition-colors">
                    Next <ChevronRight size={13} />
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      )}
      </div>
      )}
    </section>
  );
}
